import { useEffect, useState } from "react";
import { ExternalLink, MicOff, RotateCcw } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { micHelpSteps, onMicHelp } from "@/lib/micHelp";

/** App-wide "Microphone needs attention" dialog — opened by showMicHelp() from any record button. */
export default function MicHelpDialog() {
  const [help, setHelp] = useState(null);
  useEffect(() => onMicHelp(setHelp), []);
  const close = () => setHelp(null);
  const retry = () => { const fn = help?.retry; close(); fn?.(); };
  return (
    <Dialog open={Boolean(help)} onOpenChange={(o) => !o && close()}>
      <DialogContent data-testid="mic-help-dialog" className="max-w-md border-rose-400/30 bg-[#0b0e1c] text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-xl">
            <span className="grid h-9 w-9 place-items-center rounded-full bg-rose-500/20"><MicOff className="h-4 w-4 text-rose-200" /></span>
            <span data-testid="mic-help-title">{help?.title || "Microphone needs attention"}</span>
          </DialogTitle>
          <DialogDescription data-testid="mic-help-hint" className="text-white/65">{help?.hint}</DialogDescription>
        </DialogHeader>
        <ol data-testid="mic-help-steps" className="mt-1 space-y-2">
          {micHelpSteps(help?.code).map((s, i) => (
            <li key={s} className="flex gap-3 text-[13px] leading-snug text-white/85">
              <span className="grid h-5 w-5 shrink-0 place-items-center rounded-full bg-white/10 font-mono text-[10px]">{i + 1}</span>{s}
            </li>
          ))}
        </ol>
        <div className="mt-4 flex flex-wrap gap-2">
          {help?.retry ? (
            <button type="button" data-testid="mic-help-retry" onClick={retry} className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-rose-500 to-fuchsia-500 px-5 py-2.5 font-display text-sm font-semibold">
              <RotateCcw className="h-4 w-4" /> Try again
            </button>
          ) : null}
          {help?.code === "iframe-blocked" ? (
            <button type="button" data-testid="mic-help-new-tab" onClick={() => window.open(window.location.href, "_blank", "noopener")} className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-sm">
              <ExternalLink className="h-4 w-4" /> Open in a new tab
            </button>
          ) : null}
          <button type="button" data-testid="mic-help-close" onClick={close} className="rounded-full border border-white/15 px-5 py-2.5 text-sm text-white/75 hover:text-white">Close</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
